import {
  Button,
  Divider,
  EmptyStateIcon,
  Flex,
  SearchInput,
  Stack,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core'
import { CogIcon, TimesIcon } from '@patternfly/react-icons'
import { useContext, useEffect, useMemo, useState } from 'react'
import ModalConfirmationWarning from '../../components/ModalConfirmationWarning'
import ModalPCReclassification from './ModalPCReclassification'

import { Eraser, HelpCircle } from 'lucide-react'
import { AvailabilityAPI, AvailabilityPowerCurveEvents } from '../../client'
import { ActionState } from '../../components/actionState'
import EntityTable from '../../components/EntityTable'
import Select from '../../components/selects/Select'
import SitesContext from '../../contexts/SitesContext'
import useMobile from '../../hooks/useMobile'
import { queryClient } from '../../services/api'
import calcVerticalHeight from '../../utils/calcVerticalHeight'
import { formatDateTimeBrl } from '../../utils/formatDate'
import { getFirstSegment } from '../../utils/getFirstSegment'
import {
  CACHE_KEY_PC,
  DEFAULT_SEARCH_TYPE,
  DEFAULT_SEARCH_USER,
  GRID_SIZE,
  RESPONSABILIDADES,
  TABLE_HEADER_HEIGHT,
  TAB_HEADER_HEIGHT,
  TipoFilter,
  TOOLBAR_HEIGHT,
  UserFilter,
} from './constants'
import ResizeButtons from './ResizeButtons'
import useData, { keyFromDate } from './useData'

const TablePCReclassification = ({
  selectedDate,
  pcReclassification,
  resizeMode,
  setResizeMode,
}: {
  selectedDate: Date
  pcReclassification: AvailabilityPowerCurveEvents[]
  resizeMode: number
  setResizeMode: (m: number) => void
}) => {
  const { site } = useContext(SitesContext)
  const isMobile = useMobile()
  const [toggleFilters, setToggleFilters] = useState<boolean>(false)

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [dataToDelete, setDataToDelete] = useState<string>('')

  const [reclassification, setReclassification] = useState<AvailabilityPowerCurveEvents>()
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)

  const [actionState, setActionState] = useState<ActionState>('idle')
  const handleDeleteClick = (item: string) => {
    setDataToDelete(item)
    handleModalConfirmationWarningToggle()
  }
  const handleModalConfirmationWarningToggle = () => {
    setActionState('idle')
    setDeleteModalOpen(!isDeleteModalOpen)
  }

  const deleteReclassification = async (id: string) => {
    setActionState('pending')
    try {
      await AvailabilityAPI.deletePcReclassification({
        eventId: id,
        siteId: site.site_id,
      })
    } catch (error) {
      console.log(error)
      setActionState('failure')
      return
    }
    setActionState('success')
    setIsEditModalOpen(false)
    queryClient.invalidateQueries([CACHE_KEY_PC, site?.site_id, keyFromDate(selectedDate, 1)])
  }

  const handleEditReclassification = (item: AvailabilityPowerCurveEvents) => {
    setReclassification(item)
    setIsEditModalOpen(true)
  }

  const { tipos } = useData(selectedDate)

  function getTipo(id?: number) {
    const found = tipos.find(item => item.id === id)
    return found ? found.tipo : ''
  }

  function getResponsabilidade(id?: boolean) {
    const found = RESPONSABILIDADES.find(item => item.id === id)
    return found ? found.nome : ''
  }

  const buttonGroupItems = <ResizeButtons setResizeMode={setResizeMode} resizeMode={resizeMode} />

  const [searchInputUser, setSearchInputUser] = useState<UserFilter>(DEFAULT_SEARCH_USER)
  const [searchInputTipo, setSearchInputTipo] = useState<TipoFilter>(DEFAULT_SEARCH_TYPE)
  const [searchInputId, setSearchInputId] = useState<string>('')
  const [activeFilter, setActiveFilter] = useState<boolean>(false)

  const resetFilters = () => {
    setSearchInputUser(DEFAULT_SEARCH_USER)
    setSearchInputTipo(DEFAULT_SEARCH_TYPE)
    setSearchInputId('')
  }

  useEffect(() => {
    if (
      searchInputUser.username === 'Usuário' &&
      searchInputTipo.label === 'Tipo' &&
      searchInputId === ''
    ) {
      setActiveFilter(true)
    } else {
      setActiveFilter(false)
    }
  }, [searchInputUser, searchInputTipo, searchInputId])

  const curvaPotenciaData = useMemo(() => {
    if (!pcReclassification || pcReclassification?.length === 0) return []

    const _data: AvailabilityPowerCurveEvents[] = pcReclassification.filter(
      el =>
        (searchInputUser.username !== 'Usuário'
          ? el.username === searchInputUser.username
          : true) &&
        (searchInputTipo.label !== 'Tipo' ? searchInputTipo.ids.includes(el.tipo_id) : true) &&
        (searchInputId ? el.id.includes(searchInputId) : true)
    )

    return _data
  }, [pcReclassification, searchInputUser, searchInputTipo, searchInputId])

  const tiposUnicos = useMemo(() => {
    const tipoIdsPresentes = new Set(pcReclassification.map(p => p.tipo_id))
    const _tiposUnicos: {
      label: string
      ids: number[]
    }[] = []

    tipos.forEach(item => {
      if (!tipoIdsPresentes.has(item.id)) return

      const existente = _tiposUnicos.find(t => t.label === item.tipo)
      if (existente) {
        existente.ids.push(item.id)
      } else {
        _tiposUnicos.push({
          label: item.tipo,
          ids: [item.id],
        })
      }
    })
    return _tiposUnicos
  }, [pcReclassification, tipos])

  const filterGroupItems = (
    <>
      <ToolbarItem
        style={{
          ...(isMobile
            ? { padding: '4px 0', paddingBottom: '6px', flexDirection: 'column' }
            : { padding: '4px 0', gap: 4, alignItems: 'center' }),
          margin: 0,
          flex: 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
          <SearchInput
            placeholder='Filtrar por ID'
            value={searchInputId}
            onChange={(_: any, value: string) => setSearchInputId(value)}
            onClear={() => setSearchInputId('')}
            style={{
              height: '36px',
              backgroundColor: 'white',
              width: isMobile ? '89vw' : '100%',
            }}
          />
          <Tooltip
            content={
              <Stack>
                <span>
                  Deve ter somente os 8 primeiros caracteres do UUID do evento que está sendo visto
                  no banco de dados.
                </span>
              </Stack>
            }
          >
            <HelpCircle
              size={20}
              style={{
                position: 'absolute',
                right: '6px',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
          </Tooltip>
        </div>
      </ToolbarItem>
      <ToolbarItem
        style={{
          ...(isMobile ? { padding: '4px 0', paddingBottom: '6px' } : { padding: '4px 0' }),
          margin: 0,
        }}
      >
        <Select<UserFilter>
          items={[
            { id: '', username: 'Usuário', tipo: 'Usuário' },
            ...pcReclassification.filter((valor, index, self) => {
              return self.findIndex(element => element.username === valor.username) === index
            }),
          ]}
          itemValueName='username'
          itemKeyName='id'
          selected={searchInputUser}
          onChange={i => setSearchInputUser(i)}
          selectProps={{ menuAppendTo: 'parent', ...(isMobile && { width: '89vw' }) }}
          className='pf-v5-u-p-none'
        />
      </ToolbarItem>
      <ToolbarItem
        style={{
          ...(isMobile ? { padding: '4px 0', paddingBottom: '6px' } : { padding: '4px 0' }),
          margin: 0,
        }}
      >
        <Select<TipoFilter>
          items={[DEFAULT_SEARCH_TYPE, ...tiposUnicos]}
          itemValueName='label'
          itemKeyName='ids'
          selected={searchInputTipo}
          onChange={i => setSearchInputTipo(i)}
          selectProps={{ menuAppendTo: 'parent', ...(isMobile && { width: '89vw' }) }}
          className='pf-v5-u-p-none'
        />
      </ToolbarItem>
      <ToolbarItem
        style={{
          ...(isMobile
            ? { padding: '4px 0', paddingBottom: '6px' }
            : { padding: '4px 0.35rem 4px 0' }),
        }}
      >
        <Button
          variant='control'
          style={{
            ...(isMobile && { width: '89vw' }),
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
          }}
          onClick={resetFilters}
          isDisabled={activeFilter}
        >
          <Eraser size={'16px'} />
          Limpar
        </Button>
      </ToolbarItem>
    </>
  )

  const toolbarItems = (
    <div
      style={{
        display: 'flex',
        ...(isMobile && { flexDirection: 'column' }),
        width: '100%',
      }}
    >
      <ToolbarGroup
        variant='filter-group'
        style={{
          margin: 0,
          ...(isMobile && { display: 'flex', flexDirection: 'column' }),
          width: 'fit-content',
        }}
      >
        {filterGroupItems}
      </ToolbarGroup>
      <Divider
        orientation={{
          default: isMobile ? 'horizontal' : 'vertical',
        }}
        style={{
          ...(isMobile ? { width: '89vw' } : { height: '3vh', alignSelf: 'center' }),
          margin: 0,
        }}
      />
      <ToolbarGroup
        variant='button-group'
        style={{
          ...(isMobile && { display: 'flex', flexDirection: 'column' }),
          width: 'fit-content',
          margin: 0,
        }}
      >
        {buttonGroupItems}
      </ToolbarGroup>
    </div>
  )

  const tableAndContainerHeight = useMemo(() => {
    const h = calcVerticalHeight({
      elementSize: GRID_SIZE / 2 - resizeMode,
      gridSize: GRID_SIZE,
    })
    // Offset tem que ser fora, calcVerticalHeight distribui igualmente o offset entre os elementos
    const customOffset = TAB_HEADER_HEIGHT + TOOLBAR_HEIGHT
    const s = `calc(${h} - ${customOffset}rem)`

    return s
  }, [resizeMode])

  const heightBottom = useMemo(() => {
    const h = calcVerticalHeight({ elementSize: GRID_SIZE / 2 - resizeMode, gridSize: GRID_SIZE })
    // Offset tem que ser fora, calcVerticalHeight distribui igualmente o offset entre os elementos
    const customOffset = TABLE_HEADER_HEIGHT
    const s = `calc(${h} - ${customOffset}rem)`

    return s
  }, [resizeMode])

  return (
    <>
      <div style={{ height: heightBottom, backgroundColor: 'orange' }}>
        {isMobile && (
          <Flex
            style={{ justifyContent: 'flex-start', backgroundColor: 'white', position: 'relative' }}
          >
            <Button
              style={{
                backgroundColor: 'transparent',
                display: 'flex',
                gap: '4px',
                alignItems: 'center',
                justifyContent: 'center',
                height: '35px',
                color: 'black',
              }}
              onClick={() => setToggleFilters(!toggleFilters)}
            >
              {!toggleFilters ? (
                <>
                  Ações
                  <EmptyStateIcon icon={CogIcon} />
                </>
              ) : (
                <EmptyStateIcon icon={TimesIcon} />
              )}
            </Button>
          </Flex>
        )}
        {!isMobile || toggleFilters ? (
          <Flex
            style={{
              backgroundColor: 'white',
              position: 'relative',
            }}
          >
            <Toolbar
              id='toolbar-group-types'
              style={{ paddingTop: 0, paddingBottom: 0, width: '100%' }}
            >
              <ToolbarContent style={{ width: '100%' }}>{toolbarItems}</ToolbarContent>
            </Toolbar>
          </Flex>
        ) : null}

        <EntityTable<AvailabilityPowerCurveEvents>
          items={curvaPotenciaData}
          itemKeyName='id'
          isCompact
          columnConfig={[
            { key: 'id', description: 'Id', formatter: getFirstSegment },
            { key: 'ts_in', description: 'Início', formatter: v => formatDateTimeBrl(new Date(v)) },
            { key: 'ts_fin', description: 'Fim', formatter: v => formatDateTimeBrl(new Date(v)) },
            { key: 'username', description: 'Usuário' },
            { key: 'tipo_id', description: 'Tipo', formatter: id => getTipo(id) },
            {
              key: 'responsabilidade_re',
              description: 'Responsabilidade',
              formatter: id => getResponsabilidade(id),
            },
          ]}
          onClickEdit={handleEditReclassification}
          onClickDelete={e => handleDeleteClick(e.id)}
          style={{
            maxHeight: tableAndContainerHeight,
            minHeight: tableAndContainerHeight,
          }}
        />
      </div>

      <ModalConfirmationWarning<string>
        isOpen={isDeleteModalOpen}
        handleModalToggle={handleModalConfirmationWarningToggle}
        handleSubmit={id => deleteReclassification(id)}
        title='Remover Reclassificação'
        element={dataToDelete}
        actionState={actionState}
      />

      {reclassification ? (
        <ModalPCReclassification
          isOpen={isEditModalOpen}
          handleModalToggle={() => {
            setIsEditModalOpen(!isEditModalOpen)
          }}
          actionState={'idle'}
          selectedDate={selectedDate}
          dataToUpdate={reclassification}
          handleDeleteClick={handleDeleteClick}
          modalConfirmation={
            <ModalConfirmationWarning<string>
              isOpen={isDeleteModalOpen}
              handleModalToggle={handleModalConfirmationWarningToggle}
              handleSubmit={id => deleteReclassification(id)}
              title='Remover Reclassificação'
              element={dataToDelete}
              actionState={actionState}
            />
          }
        />
      ) : (
        <></>
      )}
    </>
  )
}

export default TablePCReclassification
